import type { ReactNode } from "react";
import { MessageSquare } from "lucide-react";

interface AuthLayoutProps {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * The product, in three nodes: the user's Android handset, the SmsBridge platform, the recipient.
 *
 * Inline SVG and CSS keyframes — no library. All three nodes are drawn permanently at a low,
 * constant opacity and never animate themselves; only the signal, the two route segments and the
 * confirmation tick move. The previous two-node version faded its destination in and out, which
 * left the right-hand side genuinely empty for over half of every loop.
 *
 * Restraint is the constraint. It sits opposite a login form, so: 24-34px line-art nodes at the
 * same 1.5 stroke weight as each other, one white-on-deep-orange palette, ~1.8s per hop, and
 * nothing brighter than 0.5 opacity except the signal itself.
 */
function DeliveryMotif() {
  return (
    <svg
      viewBox="0 0 300 72"
      // Purely decorative: it restates the tagline directly above it, so announcing it would be
      // noise. Not focusable, not in the accessibility tree.
      aria-hidden="true"
      focusable="false"
      className="h-[72px] w-[300px]"
    >
      <defs>
        <radialGradient id="sb-auth-halo">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.45" />
          <stop offset="55%" stopColor="#fff" stopOpacity="0.1" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </radialGradient>

        {/* Each route lights up behind the signal: a rect wider than the segment sweeps across,
            its trailing edge uncovering the lit dashes and leaving them lit. Translating a clip
            keeps this transform-only. */}
        <clipPath id="sb-auth-route1">
          <rect x="36" y="0" width="94" height="72" className="animate-route1-wipe" />
        </clipPath>
        <clipPath id="sb-auth-route2">
          <rect x="172" y="0" width="86" height="72" className="animate-route2-wipe" />
        </clipPath>
      </defs>

      {/* ---- node 1: the paired handset ---- */}
      <g className="opacity-60">
        <rect
          x="8"
          y="19"
          width="22"
          height="34"
          rx="4"
          className="fill-white/10 stroke-white"
          strokeWidth="1.5"
        />
        <rect x="15" y="24" width="8" height="1.6" rx="0.8" className="fill-white" />
      </g>

      {/* ---- route 1 ---- */}
      <path d="M36 36 H122" className="stroke-white/20" strokeWidth="1.5" strokeDasharray="4 6" strokeLinecap="round" fill="none" />
      <g className="animate-route1-fade">
        <g clipPath="url(#sb-auth-route1)">
          <path d="M36 36 H122" className="stroke-white/75" strokeWidth="1.5" strokeDasharray="4 6" strokeLinecap="round" fill="none" />
        </g>
      </g>

      {/* ---- node 2: the platform. A cloud reads instantly at 34px; a server rack does not. ---- */}
      <g className="opacity-60">
        <g transform="translate(129 19) scale(1.4167)">
          <path
            d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"
            className="fill-white/10 stroke-white"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </g>
      </g>
      {/* routing: one faint ring easing outward from the cloud */}
      <circle
        cx="146"
        cy="36"
        r="21"
        className="fill-none stroke-white animate-route-ping"
        strokeWidth="1.5"
      />

      {/* ---- route 2 ---- */}
      <path d="M172 36 H250" className="stroke-white/20" strokeWidth="1.5" strokeDasharray="4 6" strokeLinecap="round" fill="none" />
      <g className="animate-route2-fade">
        <g clipPath="url(#sb-auth-route2)">
          <path d="M172 36 H250" className="stroke-white/75" strokeWidth="1.5" strokeDasharray="4 6" strokeLinecap="round" fill="none" />
        </g>
      </g>

      {/* ---- node 3: the recipient. Present at rest; the tick is what arrives. ---- */}
      <g className="opacity-60">
        <circle cx="272" cy="36" r="15" className="fill-white/10 stroke-white" strokeWidth="1.5" />
      </g>
      <path
        d="M265 36.5 l5 5 l9 -11"
        className="stroke-white animate-confirm"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />

      {/* ---- the signal. One element makes both hops. ---- */}
      <g className="animate-signal">
        <circle cx="43" cy="36" r="14" fill="url(#sb-auth-halo)" />
        <rect x="34" y="32" width="18" height="8" rx="4" className="fill-white" />
      </g>
    </svg>
  );
}

export function AuthLayout({ title, subtitle, children, footer }: AuthLayoutProps) {
  return (
    <div className="flex min-h-screen">
      {/*
        DELIBERATE: this panel does NOT follow the app theme. It is a fixed brand surface in both
        light and dark mode.

        Reasoning: it is a marketing/brand panel, not application chrome. Its deep-orange gradient
        IS the identity, and a "light" version would either wash the orange out to near-white
        (losing the brand entirely) or keep it dark and look like a bug next to a light form. Every
        product that pairs a branded split-screen with a themed auth form does the same. White copy
        on the gradient measures 7.31:1 at the lightest stop and 15.65:1 at the darkest, so it is
        AA-compliant on its own terms regardless of the app theme.

        The FORM on the right is fully tokenised and themes normally, as does the compact orange
        lockup shown in its place below lg. So the hardcoded colours in this block are intentional
        brand values, not un-migrated hardcoded colours.

        (Original note retained: the panel was a flat bg-accent fill with white text at 2.80:1 for
        the title and 2.28:1 for the tagline, both under AA; the gradient fixed that.)
      */}
      <div className="relative hidden overflow-hidden bg-[#431407] p-12 text-white lg:flex lg:w-1/2 lg:flex-col lg:items-center lg:justify-center">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(160deg, #9A3412 0%, #7C2D12 45%, #431407 100%)",
          }}
        />

        {/* Two soft accent glows, capped at 0.22 alpha — measured as the point where white text
            over the lightest gradient stop still clears AA. Placed in the corners, away from the
            centred text block. */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(60% 50% at 85% 10%, rgba(249,115,22,0.22), transparent 70%)," +
              "radial-gradient(55% 45% at 10% 95%, rgba(234,88,12,0.20), transparent 70%)",
          }}
        />

        {/* A 32px dot grid at 4% — enough to catch the light and read as a surface rather than a
            fill, far too faint to compete with the copy. */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(rgba(255,255,255,0.16) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
            opacity: 0.25,
            maskImage: "radial-gradient(90% 70% at 50% 45%, #000 40%, transparent 100%)",
            WebkitMaskImage: "radial-gradient(90% 70% at 50% 45%, #000 40%, transparent 100%)",
          }}
        />

        <div className="relative flex flex-col items-center">
          <span className="mb-6 flex size-16 items-center justify-center rounded-card bg-white/10 ring-1 ring-inset ring-white/20 backdrop-blur-sm">
            <MessageSquare className="size-8" aria-hidden />
          </span>
          <h2 className="mb-3 text-3xl font-bold tracking-tight">SMS Fort</h2>
          {/* Raised from opacity-80: at 80% on the old flat orange this was 2.28:1. */}
          <p className="max-w-xs text-center leading-relaxed text-white/85">
            Self-hosted SMS gateway. Send messages through your own Android device.
          </p>

          <div className="mt-10">
            <DeliveryMotif />
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center bg-bg px-6 py-12">
        <div className="mb-8 flex items-center gap-2.5 lg:hidden">
          <span className="flex size-9 items-center justify-center rounded-control bg-accent">
            <MessageSquare className="size-5 text-white" aria-hidden />
          </span>
          <span className="text-lg font-semibold tracking-tight text-text">SMS Fort</span>
        </div>

        <div className="w-full max-w-[400px]">
          <h1 className="mb-1 text-2xl font-semibold tracking-tight text-text">{title}</h1>
          <p className="mb-8 text-sm text-text-muted">{subtitle}</p>
          {children}
          {footer && <div className="mt-6 text-center text-sm text-text-muted">{footer}</div>}
        </div>
      </div>
    </div>
  );
}
